import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { pricingI18n, commonI18n } from "@/utils/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getCachedSubscriptionPlans, getSubscriptionPlans, type PlansApiResponse } from "@/services/api/subscriptions";
import { usePlanSelectionStore, type PlanType } from "@/stores/planSelectionStore";
import { cn } from "@/lib/utils";
import { UserHeader } from "@/components/customer/UserHeader";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { useAuth } from "@/hooks/useAuth";
import { getEvent, getEvents, getTrashedEvents, restoreEvent, type Event } from "@/services/api/events";
import { toast } from "sonner";
import { USER_ROUTES } from "@/routes";
import { PurchaseEventPicker } from "@/components/customer/purchases/PurchaseEventPicker";
import { eventPlanOf, resolvePurchaseEvent } from "@/utils/purchaseEvent";

export default function PachetePage() {
  const { t, currentLanguage } = useTranslation();

  const locale = useRouteLocale();

  const location = useLocation();
  usePageMeta({
    title: t("pricing.metaTitle"),
    description: t("pricing.metaTitle"),
    locale: locale,
    path: location.pathname,
    noindex: true,
  });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const currentLocale = locale || 'ro';
  const localeCode = currentLanguage as 'ro' | 'en';
  const { setSelectedPlan } = usePlanSelectionStore();
  const isAdmin = user?.user_admin ?? false;

  const [plansData, setPlansData] = useState<PlansApiResponse | null>(null);
  const [events, setEvents] = useState<Event[] | null>(null);
  // JUB-155/JUB-157 D2: a failed list is an error, never an empty account —
  // "create the event first" on a transient failure invites a duplicate event.
  const [eventsError, setEventsError] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlansData(await getSubscriptionPlans());
      } catch (error) {
        console.error('Failed to fetch plans:', error);
        setPlansData(getCachedSubscriptionPlans() ?? {
          // Keep the page rendering if the API and the cache both fail.
          plans: {
            free: { price: 0, currency: 'RON', features: [] },
            standard: { price: 0, currency: 'RON', features: [] },
            premium: { price: 0, currency: 'RON', features: [] },
          },
          currentPlan: 'free',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (isAdmin) return;
    let cancelled = false;
    setEventsError(false);
    getEvents()
      .then((list) => { if (!cancelled) setEvents(list); })
      .catch(() => {
        if (!cancelled) {
          setEvents(null);
          setEventsError(true);
        }
      });
    return () => { cancelled = true; };
  }, [isAdmin, reloadKey]);

  // JUB-157: a plan is bought for exactly one event. JUB-155: the plan shown
  // as current is that event's plan — plans no longer belong to the account.
  const resolution = events ? resolvePurchaseEvent(events, searchParams.get('event')) : null;

  // F9: an explicit ?event= that is not in the active list is looked up as
  // itself — archived (still purchasable), in the Trash (restore first) or
  // unavailable — and never replaced by another event.
  const [explicit, setExplicit] = useState<
    { uuid: string; state: 'loading' | 'archived' | 'trashed' | 'missing'; event?: Event } | null
  >(null);
  const unavailableUuid = resolution?.kind === 'unavailable' ? resolution.uuid : null;
  useEffect(() => {
    if (!unavailableUuid) {
      setExplicit(null);
      return undefined;
    }
    let cancelled = false;
    setExplicit({ uuid: unavailableUuid, state: 'loading' });
    (async () => {
      try {
        const event = await getEvent(unavailableUuid);
        if (!cancelled) setExplicit({ uuid: unavailableUuid, state: 'archived', event });
        return;
      } catch {
        // not an active event of this account: maybe in the Trash
      }
      try {
        const trashed = (await getTrashedEvents()).find((event) => event.uuid === unavailableUuid);
        if (!cancelled) {
          setExplicit(trashed ? { uuid: unavailableUuid, state: 'trashed', event: trashed } : { uuid: unavailableUuid, state: 'missing' });
        }
      } catch {
        if (!cancelled) setExplicit({ uuid: unavailableUuid, state: 'missing' });
      }
    })();
    return () => { cancelled = true; };
  }, [unavailableUuid]);

  const purchaseEvent = resolution?.kind === 'event'
    ? events?.find((event) => event.uuid === resolution.uuid) ?? null
    : explicit?.state === 'archived' && explicit.event
      ? explicit.event
      : null;

  const handleRestoreExplicit = async () => {
    if (!explicit?.event) return;
    try {
      await restoreEvent(explicit.event.uuid);
      toast.success(t('events.trash.restored'));
      setReloadKey((k) => k + 1);
    } catch {
      toast.error(t('events.trash.restoreError'));
    }
  };
  const currentPlan: PlanType | null = isAdmin || !purchaseEvent ? null : eventPlanOf(purchaseEvent);

  const selectEvent = (uuid: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('event', uuid);
    setSearchParams(next, { replace: true });
  };

  const handleSelectPlan = (plan: PlanType) => {
    if (!purchaseEvent) return;
    setSelectedPlan(plan, 'pachete');
    const params = new URLSearchParams({ plan, event: purchaseEvent.uuid });
    navigate(`/${currentLocale}/payment?${params.toString()}`);
  };

  const getPlanPrice = (plan: PlanType): string => {
    if (!plansData) return '—';
    const apiPlan = plansData.plans[plan];
    // Use landing.pricing first, then fallback to pricing
    const priceTemplate = t(`landing.pricing.${plan}.priceTemplate`) || t(`pricing.${plan}.priceTemplate`) || '{{price}} {{currency}}';
    return priceTemplate
      .replace('{{price}}', apiPlan.price.toString())
      .replace('{{currency}}', apiPlan.currency);
  };

  const getPlanFeatures = (plan: PlanType): string[] => {
    if (!plansData) return [];
    const apiPlan = plansData.plans[plan];
    // Use the feature codes from the API response and translate them
    // First try landing.pricing.pachete section, then pricing.pachete, then features section
    return apiPlan.features.map((featureCode: string) => {
      // Try landing.pricing.{plan}.pachete.{featureCode} first (matches landing pricing structure)
      const landingPacheteKey = `landing.pricing.${plan}.pachete.${featureCode}`;
      const landingPacheteTranslation = t(landingPacheteKey) || '';
      if (landingPacheteTranslation && landingPacheteTranslation !== landingPacheteKey) {
        return landingPacheteTranslation;
      }
      
      // Fallback to pricing.{plan}.pachete.{featureCode}
      const pacheteKey = `pricing.${plan}.pachete.${featureCode}`;
      const pacheteTranslation = t(pacheteKey) || '';
      if (pacheteTranslation && pacheteTranslation !== pacheteKey) {
        return pacheteTranslation;
      }
      
      // Last fallback to features section
      const featuresKey = `pricing.${plan}.features.${featureCode}`;
      const featuresTranslation = t(featuresKey) || featureCode;
      return featuresTranslation !== featuresKey ? featuresTranslation : featureCode;
    });
  };

  const plans: PlanType[] = ['free', 'standard', 'premium'];

  // Helper function to check if a plan's CTA should be hidden
  // Hide CTAs for lower-level packages than the user's current plan
  const shouldHideCTA = (plan: PlanType): boolean => {
    if (isAdmin || !currentPlan) return false;
    
    // Plan hierarchy: free < standard < premium
    const planHierarchy: Record<PlanType, number> = {
      free: 0,
      standard: 1,
      premium: 2,
    };
    
    const currentPlanLevel = planHierarchy[currentPlan];
    const targetPlanLevel = planHierarchy[plan];
    
    // Hide CTA if target plan is lower than current plan
    return targetPlanLevel < currentPlanLevel;
  };

  return (
    <div className="min-h-screen bg-background">
      {isAdmin ? (
        <AdminHeader currentLocale={currentLocale} active="management" />
      ) : (
        <UserHeader currentLocale={currentLocale} active="pachete" />
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 pt-12 pb-8">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-2">{t('landing.pricing.title') || pricingI18n.get('landing.pricing.title', localeCode)}</h2>
            <p className="text-muted-foreground">{t('landing.pricing.subtitle') || t('pricing.subtitle') || 'Alege planul potrivit pentru nunta ta'}</p>
            {/* JUB-27: plans are per event, not per account — say it where the prices are. */}
            <p
              className="mt-4 mx-auto max-w-2xl rounded-lg border border-pink-200 bg-pink-50 dark:border-pink-900 dark:bg-pink-950/20 px-4 py-3 text-sm text-pink-900 dark:text-pink-100"
              data-testid="pricing-per-event-note"
            >
              {t('landing.pricing.perEventNotePachete')}
            </p>
            {!isAdmin && eventsError && (
              <div
                className="mx-auto mt-4 max-w-md rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm"
                role="alert"
                data-testid="pricing-events-error"
              >
                <p>{t('landing.pricing.eventsLoadError')}</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => setReloadKey((k) => k + 1)}
                  data-testid="pricing-events-retry"
                >
                  {t('common.retry')}
                </Button>
              </div>
            )}
            {!isAdmin && purchaseEvent && (
              <p className="mt-2 text-sm text-muted-foreground" data-testid="pricing-applies-to-event">
                {t('landing.pricing.appliesToEvent', { name: purchaseEvent.name })}
              </p>
            )}
            {!isAdmin && explicit?.state === 'archived' && explicit.event && (
              <p className="mt-2 text-sm text-muted-foreground" data-testid="pricing-event-archived">
                {t('landing.pricing.eventArchived', { name: explicit.event.name })}
              </p>
            )}
            {!isAdmin && explicit?.state === 'trashed' && explicit.event && (
              <div className="mx-auto mt-4 max-w-md rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 text-sm" data-testid="pricing-event-in-trash">
                <p>{t('landing.pricing.eventInTrash', { name: explicit.event.name })}</p>
                <Button size="sm" variant="outline" className="mt-2" onClick={handleRestoreExplicit} data-testid="pricing-restore-event">
                  {t('purchases.restoreAction')}
                </Button>
              </div>
            )}
            {!isAdmin && explicit?.state === 'missing' && (
              <p className="mx-auto mt-4 max-w-md rounded-lg border px-4 py-3 text-sm" data-testid="pricing-event-unavailable">
                {t('landing.pricing.eventUnavailable')}
              </p>
            )}
            {!isAdmin && events && (events.length > 1 || (explicit?.state === 'missing' && events.length > 0)) && (
              <PurchaseEventPicker
                events={events}
                selectedUuid={purchaseEvent?.uuid ?? null}
                onSelect={selectEvent}
              />
            )}
            {!isAdmin && (resolution?.kind === 'none' || (explicit?.state === 'missing' && events?.length === 0)) && (
              <div className="mx-auto mt-4 max-w-md rounded-lg border px-4 py-3 text-sm" data-testid="pricing-create-event-first">
                <p className="text-muted-foreground">{t('landing.pricing.createEventFirstDescription')}</p>
                <Button
                  className="mt-3 bg-pink-500 hover:bg-pink-600 text-white"
                  onClick={() => navigate(USER_ROUTES.events(currentLocale))}
                >
                  {t('landing.pricing.createEventFirst')}
                </Button>
              </div>
            )}
          </div>

          {/* Plans Grid */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t('common.loading') || commonI18n.get('common.loading', localeCode)}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {plans.map((plan) => {
                // Hide "most popular" marker for standard plan if user has premium account
                const isPopular = plan === 'standard' && currentPlan !== 'premium';
                const isCurrentPlan = !isAdmin && plan === currentPlan;
                const price = getPlanPrice(plan);
                const features = getPlanFeatures(plan);
                const planName = t(`landing.pricing.${plan}.name`) || t(`pricing.${plan}.name`) || (plan === 'free' ? 'Gratuit' : plan === 'standard' ? 'Standard' : 'Premium');
                const description = t(`landing.pricing.${plan}.description`) || t(`pricing.${plan}.description`) || '';
                const needsEvent = !isAdmin && plan !== 'free' && !purchaseEvent;
                const cta = isCurrentPlan
                  ? t('payments.currentPlan') || 'Plan curent'
                  : needsEvent
                    ? t('landing.pricing.selectEventFirst')
                    : t(`landing.pricing.${plan}.cta`) || t(`pricing.${plan}.cta`) || 'Alege planul';

                return (
                  <Card
                    key={plan}
                    className={cn(
                      "relative flex flex-col rounded-xl",
                      isPopular && "border-2 border-pink-500 shadow-lg",
                      isCurrentPlan && !isPopular && "ring-2 ring-green-500",
                      !isPopular && !isCurrentPlan && "border-0 shadow-sm"
                    )}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                        <span className="bg-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                          {t('landing.pricing.standard.popular') || pricingI18n.get('landing.pricing.standard.popular', currentLocale as 'ro' | 'en')}
                        </span>
                      </div>
                    )}
                    <CardHeader className="text-center pb-4 pt-6">
                      <CardTitle className="text-2xl font-bold mb-2">{planName}</CardTitle>
                      <div className="text-3xl font-bold mb-2 text-pink-600">{price}</div>
                      <CardDescription className="text-gray-600 dark:text-gray-400">{description}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col px-6 pb-6">
                      <ul className="space-y-3 mb-6 flex-1">
                        {features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2">
                            {/* Rounded bullet point with inner circle */}
                            <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-pink-500 flex items-center justify-center">
                              <div className="w-2 h-2 rounded-full bg-pink-500" />
                            </div>
                            <span className="text-base text-[rgb(132,98,109)] leading-6 font-normal" style={{ fontFamily: 'Inter, sans-serif' }}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      {!isAdmin && !shouldHideCTA(plan) && (plan !== 'free' || isCurrentPlan) && (
                        <Button
                          onClick={() => !isCurrentPlan && handleSelectPlan(plan)}
                          disabled={isCurrentPlan || needsEvent}
                          data-testid={`pachete-cta-${plan}`}
                          className={cn(
                            "w-full rounded-lg",
                            isPopular && !isCurrentPlan && "bg-pink-500 hover:bg-pink-600 text-white shadow-lg shadow-pink-500/50",
                            isCurrentPlan && "bg-green-100 text-green-800 border border-green-300 disabled:opacity-100 cursor-not-allowed",
                            !isPopular && !isCurrentPlan && "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                          )}
                          variant={isPopular && !isCurrentPlan ? "default" : isCurrentPlan ? "default" : "outline"}
                        >
                          {cta}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

